import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import {
  ProgressBarWrapper,
  Track,
  Thumb,
} from './styles';
import Fill from './Fill';
import BufferedBar from './BufferedBar';
import AvailableContentBar from './AvailableContentBar';
import Slider from './Slider';
import Thumbnail from './Thumbnail';
import { usePlayer } from '../../../PlayerContext';
import AdPoints from './AdPoints';
import { selectNormalizedInterval } from '../../../selectors';
import platform, { OS_TYPES } from '../../../../player/utils/platform';

const PRELOAD_IMG_DELAY = 3 * 1000;
const THUMB_SIZE = 18;

const TILES_ROW = 8;
const TILES_COLUMN = 8;

const preloadThumbnails = (player, id, time, numOfTiles = TILES_ROW * TILES_COLUMN) => {
  player.getThumbnails(id, time).then((th) => {
    if (!th) return;

    const img = new Image();
    img.src = th.uris[0];
    const durationPerImage = numOfTiles * th.duration;
    img.onload = () => {
      preloadThumbnails(player, id, time + durationPerImage, numOfTiles);
    };
  });
};

function ProgressBar(props) {
  const _thumb = useRef();
  const _track = useRef();
  const [trackWidth, setTrackWidth] = useState(_track.current?.offsetWidth || 100);

  const {
    isPlayingAds,
    isAirPlaying,
    controlsVisible,
    seekEnabled,
    isLiveEvent,
    playing,
    progress,
    duration,
  } = props;

  const thumbOffsetPx = trackWidth * progress;

  const thickMode = isPlayingAds && !controlsVisible && playing;

  // time-trickplay tooltip
  const player = usePlayer();
  const timelineTag = useRef();
  const [trackId, setTrackId] = useState(null); // thumbnail track id
  const [hoverTime, setHoverTime] = useState(null);
  const [tooltipPosX, setTooltipPosX] = useState(0);
  const [thumbnail, setThumbnail] = useState(null);
  const adsPointsOffset = player.adsPlayer?._adsPointsOffset || [];
  const { isLive, rights } = useSelector(state => ({
    isLive: state.playback.isLive,
    rights: state.playback.rights,
  }));
  const [start] = useSelector(selectNormalizedInterval, shallowEqual);
  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);

  const showThumbnail = (e) => {
    if (!duration) return;

    const { left, width } = e.target.getBoundingClientRect(); // Get range position and width
    const min = parseFloat(e.target.min);
    const max = parseFloat(e.target.max);
    const step = parseFloat(e.target.step);

    // Calculate time based on mouse position over the range
    const position = e.clientX - left;
    let time = min + ((position / width) * (max - min));
    // Round to nearest step
    time = Math.round(time / step) * step;

    const thumbnailTime = isLive ? player.currentPlayer._absoluteTimeToPlayerTime(start + time) : time;

    setHoverTime(time);
    setTooltipPosX(position);

    if (trackId !== null) {
      player?.currentPlayer?.videoPlayer?.getThumbnails(trackId, thumbnailTime).then(setThumbnail);
    }
  };

  const hideThumbnail = () => {
    setHoverTime(null);
    setThumbnail(null);
  };

  useEffect(() => {
    const tracks = player?.currentPlayer?.videoPlayer?.getImageTracks();
    let timeOut;

    if (tracks?.length && rights.rewind && !isMobile) {
      setTrackId(tracks[0].id);
      // Get number of tiles per thumbnail image
      // The value is a grid-item-dimension consisting of two positive decimal
      // integers in the format: column-x-row ('4x3'). It describes the arrangement
      // of Images in a Grid. The minimum valid LAYOUT is '1x1'.
      const tilesLayout = tracks[0]?.tilesLayout || `${TILES_COLUMN}x${TILES_ROW}`;
      const [col, row] = tilesLayout.split('x').map(Number);
      const columns = col || TILES_COLUMN;
      const rows = row || TILES_ROW;
      const numOfTiles = columns * rows;

      timeOut = setTimeout(() => {
        if (player?.currentPlayer?.videoPlayer) {
          if (!isLiveEvent && !isLive) {
            // do not preload thumbnails for live event and live playback
            preloadThumbnails(player.currentPlayer.videoPlayer, tracks[0].id, 0, numOfTiles);
          }
        }
      }, PRELOAD_IMG_DELAY);
    } else {
      setTrackId(null);
      setThumbnail(null);
    }

    return () => {
      clearTimeout(timeOut);
    };
  }, [duration]);

  useEffect(() => {
    setTrackWidth(_track.current?.offsetWidth || 100);

    if (!_track.current) return () => null;

    const resizeObserver = new ResizeObserver(() => {
      setTrackWidth(_track.current?.offsetWidth || 100);
    });

    resizeObserver.observe(_track.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [_track.current]);

  const fillBarColor = useMemo(() => {
    if (isPlayingAds) return 'ads';
    if (isLiveEvent) return 'progressBarFillColorLive';
    return 'progressBarFillColor';
  }, [isPlayingAds, isLiveEvent]);

  return (
    <ProgressBarWrapper>
      <Track ref={_track} thick={thickMode}>
        <AvailableContentBar />

        <BufferedBar />

        <Fill
          color={fillBarColor}
          left="0"
          width={`${progress * 100}%`}
        />

        <AdPoints
          disabled={isPlayingAds || isAirPlaying}
          adsPointsOffset={adsPointsOffset}
          width={8}
        />

        {seekEnabled && (
          <Thumb
            ref={_thumb}
            style={{ left: `${thumbOffsetPx}px` }}
            className="thumb"
            height={THUMB_SIZE}
            width={THUMB_SIZE}
          />
        )}

        <Thumbnail
          hoverTime={hoverTime}
          tooltipPosX={tooltipPosX}
          thumbnail={thumbnail}
        />

        <Slider
          ref={timelineTag}
          onMouseMove={showThumbnail}
          onMouseOut={hideThumbnail}
          thumbHeight={THUMB_SIZE}
          thumbWidth={THUMB_SIZE}
        />
      </Track>
    </ProgressBarWrapper>
  );
}

ProgressBar.propTypes = {
  isPlayingAds: PropTypes.bool,
  isAirPlaying: PropTypes.bool,
  controlsVisible: PropTypes.bool,
  isLiveEvent: PropTypes.bool,
  playing: PropTypes.bool,
  seekEnabled: PropTypes.bool.isRequired,
  progress: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
};

export default React.memo(ProgressBar);
